import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { auth, db } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null); // بيانات المصادقة الأساسية
  const [userProfile, setUserProfile] = useState(null); // بيانات الملف الشخصي من Firestore
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        setLoading(true);
        setError(null);
        
        if (firebaseUser) {
          // حفظ بيانات المصادقة الأساسية
          setAuthUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL
          });

          // جلب بيانات الملف الشخصي الإضافية
          const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          
          if (userDoc.exists()) {
            setUserProfile(userDoc.data());
          } else {
            // إنشاء ملف افتراضي إذا لم يوجد
            const defaultProfile = {
              role: 'student',
              createdAt: new Date(),
              subscribedCourses: [],
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || '',
              photoURL: firebaseUser.photoURL || ''
            };
            setUserProfile(defaultProfile);
            
            // حفظ الملف الافتراضي في Firestore
            await setDoc(doc(db, "users", firebaseUser.uid), defaultProfile);
          }
        } else {
          setAuthUser(null);
          setUserProfile(null);
        }
      } catch (err) {
        console.error("Authentication error:", err);
        setError(err.message);
        setAuthUser(null);
        setUserProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setAuthUser(null);
      setUserProfile(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!authUser?.uid) return;
      
      await updateDoc(doc(db, "users", authUser.uid), updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const value = useMemo(() => ({
    // بيانات المصادقة الأساسية
    auth: authUser,
    // بيانات الملف الشخصي
    profile: userProfile,
    // دمج بعض الخصائص الشائعة الاستخدام
    user: authUser ? {
      ...authUser,
      ...userProfile,
      // خصائص مختصرة
      isAdmin: userProfile?.role === 'admin',
      isTeacher: userProfile?.role === 'teacher',
      isStudent: userProfile?.role === 'student' || !userProfile?.role,
      // دالة للتحديث
      update: updateProfile
    } : null,
    // حالات النظام
    loading,
    error,
    // دوال
    logout,
    updateProfile
  }), [authUser, userProfile, loading, error]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};